// src/components/store/NewRefillsComponents/MultiAddressOrderSummary.jsx
import React, { useState } from "react";
import { MapPin, Calendar } from "lucide-react";
import DividerComponent from "../../ui/DividerComponent";
import { CheckTermsComponent } from "../NewItemsComponents/CheckTermsComponent";
import {
  formatPrice,
  formatDate,
  formatTimeSlot,
} from "../../../scripts/generalScript";
import { useSalesContext } from "../../../contexts/SalesContext";

const MultiAddressOrderSummary = ({
  groupedByAddress = {},
  totals = {},
  addressValidation = {}, // ← AGREGAR
  selectedDate,
  termsAccepted, // Agregar
  setTermsAccepted, // Agregar
}) => {
  const [imageUrls, setImageUrls] = useState({});
  const { selectedDeliveryDate, selectedTimeSlot } = useSalesContext();
  // Calcular totales con valores por defecto
  const calculatedTotals = React.useMemo(
    () => ({
      itemCount: 0,
      subtotal: 0,
      deliveryCost: 0,
      total: 0,
      ...totals,
    }),
    [totals]
  );

  // Efecto para manejar las imágenes
  React.useEffect(() => {
    const urls = {};
    Object.entries(groupedByAddress).forEach(([addressId, addressData]) => {
      const imageAddress = addressData[0].address_image_url;
      if (imageAddress) {
        if (imageAddress instanceof Blob) {
          urls[addressId] = URL.createObjectURL(imageAddress);
        } else if (typeof imageAddress === "string") {
          urls[addressId] = imageAddress;
        }
      }
    });
    setImageUrls(urls);

    // Cleanup
    return () => {
      Object.values(urls).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [groupedByAddress]);

  // Componente de información de entrega por dirección
  const DeliveryInfoCard = ({ addressId, addressData, index }) => {
    // Manejar tanto arrays directos como objetos con estructura
    const isArray = Array.isArray(addressData);
    const items = isArray ? addressData : addressData.items || [];
    const address = isArray ? addressId : addressData.address || addressId;
    const city = isArray ? "" : addressData.city || "";
    const subtotal = isArray
      ? items.reduce(
          (sum, item) => sum + (item.price || 0) * (item.quantity || 1),
          0
        )
      : addressData.subtotal || 0;
    const deliveryCost = addressValidation[addressId]?.cost || 0;
    const addressTotal = subtotal + deliveryCost;
    const imageUrl = imageUrls[addressId];

    return (
      <div className="bg-white rounded-xl ">
        <h2 className="text-xl font-medium text-gray-800 mb-4">
          Entrega #{index + 1}
        </h2>

        {/* Dirección */}
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 mt-1">
            <MapPin className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1">
            <span className="font-normal text-base text-gray-600 block leading-relaxed">
              {address}
            </span>
            {city && <span className="text-sm text-gray-500">{city}</span>}
          </div>
        </div>

        {/* Imagen de la dirección */}
        {imageUrl && (
          <div className="mb-4">
            <img
              src={imageUrl}
              alt={`Dirección ${index + 1}`}
              className="rounded-xl object-cover h-48 w-full"
              onError={(e) => {
                console.error("Error cargando imagen:", e);
                e.target.style.display = "none";
              }}
            />
          </div>
        )}

        <div className="space-y-3 mb-8">
          {items.map((item, itemIndex) => (
            <div
              key={item.jugId || item.jug_id || itemIndex}
              className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center text-sm font-medium text-gray-700">
                  {item.quantity || 1}
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    Botellón 20L - Recambio
                  </p>
                  <p className="text-sm text-gray-500">
                    {item.barcode ||
                      item.jugName ||
                      `ID: ${item.jugId || item.jug_id}`}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900 text-base">
                  {"$" +
                    formatPrice((item.price || 0) * (item.quantity || 1)) +
                    " "}
                </p>
                {item.quantity > 1 && (
                  <p className="text-sm text-gray-500">
                    {"$" + formatPrice(item.price || "$0")} c/u
                  </p>
                )}
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
            <span className="text-gray-600">Costo de entrega</span>
            <span className="font-medium text-gray-900">
              {deliveryCost > 0 ? "$" + formatPrice(deliveryCost) : "$0"}
            </span>
          </div>
        </div>
        <DividerComponent
          size={8}
          color="f4f4f4"
          margin={16}
          direction="horizontal"
        />
      </div>
    );
  };

  // Componente de resumen financiero
  const FinancialSummary = () => (
    <div className="bg-gray-50 p-6 rounded-xl h-fit">
      <h3 className="text-lg font-medium mb-6">Resumen del pedido</h3>
      <div className="flex flex-col gap-4 text-base">
        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
          <div className="flex flex-col">
            <span className="text-gray-800">Productos</span>
            <span className="text-sm text-gray-600">
              $15.000 x {calculatedTotals.itemCount} ud.
            </span>
          </div>
          <div className="text-right">
            <span className="font-normal text-xl text-gray-800">
              {"$" + formatPrice(calculatedTotals.subtotal)}
            </span>
          </div>
        </div>

        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
          <div className="flex flex-col">
            <span className="text-gray-800">Costo de entrega</span>
            <span className="text-sm text-gray-600">
              Por todas las direcciones
            </span>
          </div>
          <div className="text-right">
            <span className="font-normal text-xl text-gray-800">
              {"$" + formatPrice(calculatedTotals.deliveryCost)}
            </span>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between items-center">
            <span className="text-xl font-medium text-gray-900">Total</span>
            <span className="text-2xl font-medium">
              {"$" + formatPrice(calculatedTotals.total)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-8 max-h-screen">
      {/* Layout móvil: Una columna */}
      <div className="lg:hidden">
        {/* Fecha de entrega global */}
        {selectedDeliveryDate && (
          <div>
            <h3 className="text-lg font-medium mb-4">Fecha de entrega</h3>
            <div className="bg-gray-50 p-4 rounded-xl">
              <div className="flex items-start gap-3">
                <Calendar
                  className="text-gray-500 mt-1 flex-shrink-0"
                  size={20}
                />
                <div className="flex gap-2">
                  <span className="text-base font-medium text-gray-800 block">
                    {formatDate(selectedDeliveryDate)}
                  </span>
                  <span className="text-base text-gray-600">
                    Por la {formatTimeSlot(selectedTimeSlot)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <DividerComponent
          size={1}
          color="ebebeb"
          margin={16}
          direction="horizontal"
        />

        {/* Entregas por dirección */}
        <div className="space-y-6">
          {Object.entries(groupedByAddress).map(
            ([addressId, addressData], index) => (
              <DeliveryInfoCard
                key={addressId}
                addressId={addressId}
                addressData={addressData}
                index={index}
              />
            )
          )}
        </div>

        <DividerComponent
          size={1}
          color="ebebeb"
          margin={16}
          direction="horizontal"
        />

        {/* Resumen financiero */}
        <FinancialSummary />

        <DividerComponent
          size={1}
          color="ebebeb"
          margin={16}
          direction="horizontal"
        />

        {/* Términos y condiciones */}
        <CheckTermsComponent
          checked={termsAccepted}
          onChange={setTermsAccepted} // ← Pasar directamente la función
        />
      </div>

      {/* Layout desktop: Dos columnas */}
      <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-start">
        {/* Columna izquierda - Información de entregas */}
        <div className="space-y-8 max-h-full pb-40">
          {/* Fecha de entrega global */}
          {selectedDeliveryDate && (
            <div>
              <h3 className="text-lg font-medium mb-4">Fecha de entrega</h3>
              <div className="bg-gray-50 p-4 rounded-xl">
                <div className="flex items-start gap-3">
                  <Calendar
                    className="text-gray-500 mt-1 flex-shrink-0"
                    size={20}
                  />
                  <div className="flex gap-2">
                    <span className="text-base font-medium text-gray-800 block">
                      {formatDate(selectedDeliveryDate)}
                    </span>
                    <span className="text-base text-gray-600">
                      Por la {formatTimeSlot(selectedTimeSlot)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Entregas por dirección */}
          {Object.entries(groupedByAddress).map(
            ([addressId, addressData], index) => (
              <DeliveryInfoCard
                key={addressId}
                addressId={addressId}
                addressData={addressData}
                index={index}
              />
            )
          )}
        </div>

        {/* Columna derecha - Resumen y términos */}
        <div className="space-y-6 lg:sticky lg:top-8">
          <FinancialSummary />
          <CheckTermsComponent
            checked={termsAccepted}
            onChange={setTermsAccepted} // ← SIN (e) => - pasar la función directamente
          />
        </div>
      </div>
    </div>
  );
};

export default MultiAddressOrderSummary;
